<template>
  <div :class="$style['product-sizes']">
    <div
      v-if="sizes"
      :class="$style['product-sizes__title']">
      <b>{{ $t('general/filter-size') }}</b>
      <div
        v-if="sizeType?.type ==='shoes' "
        :class="$style['product-sizes__conversion']">
        <Btn
          v-for="sizeLang in ['EU', 'UK', 'US']"
          :key="sizeLang"
          :color="selectedSizeLang === sizeLang ? 'link-active' : 'link'"
          size="small"
          no-padding="x"
          :class="{ [$style['button--selected']]: selectedSizeLang === sizeLang }"
          @click="selectedSizeLang = sizeLang">
          {{ sizeLang }}
        </Btn>
      </div>
    </div>

    <div
      v-if="sizes && sizes.length > 1 && sizeType?.type !== 'width-height'"
      data-test-id="product-sizes"
      :class="$style['product-sizes__buttons']">
      <Btn
        v-for="size in sizes"
        :key="size.id"
        :data-test-id="size.stock ? 'size-button' : 'size-button-unavailable'"
        :color="currentSize && currentSize.size === size.size && currentSize.stock ? 'cta' : !size.stock ? 'unavailable' : null"
        @click="!size.stock ?? $emit( 'show-subscription-form'); $emit('input', size)">
        {{ sizeLabel(size.size) }}
      </Btn>

      <!-- Subscribe toggle button -->
      <Btn
        v-if="showSubscriptionButton"
        color="link"
        size="small"
        no-padding="x"
        class="h-margin-left"
        @click="$emit('show-subscription-form')">
        {{ $t('product/size-missing') }}
      </Btn>
    </div>

    <!-- Size jeans variations-->
    <div v-if="sizes && sizes.length > 1 && sizeType?.type === 'width-height'" data-test-id="product-sizes-width-height">
      <div v-for="(title, type) in sizeType.values" :key="type.key" :class="$style['product-sizes__buttons']">
        {{ title }}<br>
        <Btn
          v-for="size in sizesByType(type)"
          :key="size.id"
          :data-test-id="`product-size-${type}`"
          :color="isSelectedSizeVariation(type, size) ? 'cta' : !hasStock(type, size)? 'unavailable' : null"
          @click="hasStock(type, size) ? selectSizeVariation(type, size) : outOfStock(type);">
          {{ size }}
        </Btn>
        <Btn
          v-if="showSubscriptionButton && type ==='height'"
          color="link"
          size="small"
          no-padding="x"
          class="h-margin-left"
          @click="$emit('show-subscription-form')">
          {{ $t('product/size-missing') }}
        </Btn>
      </div>
    </div>

    <div v-if="sizes && sizes.length === 1" class="h-margin-bottom-xl h-color-grey-dark">
      {{ sizes[0].size }}
    </div>

    <div
      v-if="product?.sizeNotice"
      :class="$style['product-sizes__notice']">
      {{ product.sizeNotice.title }}
    </div>

    <!-- Size errors -->
    <div
      v-if="errors && errors.size"
      class="h-color-error h-font-size-s">
      {{ errors.size }}
    </div>
  </div>
</template>
<script setup>
import sizeConversion from '~/helpers/size-conversion'
const emit = defineEmits(['input', 'show-subscription-form'])
const selectedSizeLang = ref('EU')
const selectedSizeVariations = ref({})
const stockMatrix = ref(new Map())
const props = defineProps({
  currentSize: {
    type: Object,
    default: null
  },
  product: {
    type: Object,
    default: null
  },
  showSubscriptionButton: {
    type: Boolean,
    default: false
  },
  sizes: {
    type: [Array, Object, Boolean],
    default: null
  },
  sizeType: {
    type: Object,
    default: null
  },
  errors: {
    type: Object,
    default: null
  }
})

// If sizeType is provided. Create a stockMatrix. Just for jeanse selector
if (props.sizes && props.sizeType?.type === 'width-height') {
  props.sizes.forEach(size => {
    stockMatrix.value.set(Object.values(size.variations).join('-'), size.stock)
  })
}

const sizeLabel = label => {
  if (sizeConversion[label] === undefined) { return label }
  return sizeConversion[label][selectedSizeLang.value]
}

const sizesByType = type => {
  const returnData = new Set()

  props.sizes.forEach(size => {
    returnData.add(size.variations[type])
  })

  return returnData
}

const isSelectedSizeVariation = (type, size) => {
  return selectedSizeVariations.value[type] === size
}

const selectSizeVariation = (type, size) => {
  selectedSizeVariations.value[type] = size

  if (!hasStock(type, size)) {
    Object.keys(selectedSizeVariations.value).forEach(key => {
      delete selectedSizeVariations.value[key]
    })
  }

  // Check if selected variations are the same length as the required variations
  if (Object.keys(selectedSizeVariations.value).length === Object.keys(props.sizeType.values).length) {
    const foundSize = props.sizes.find(sizeItem => {
      // @TODO: Make this part modular. Should be based on what's given in props.sizeType
      return sizeItem.variations.width === selectedSizeVariations.value.width &&
      sizeItem.variations.height === selectedSizeVariations.value.height
    })

    emit('input', foundSize)
  }
}

// Get stock in default state. Not active selection
const hasStock = (type, size) => {
  if (type === 'height' && selectedSizeVariations.value.width) {
    return stockMatrix.value.get(`${selectedSizeVariations.value.width}-${size}`)
  }
  if (type === 'width' && selectedSizeVariations.value.height) {
    return stockMatrix.value.get(`${size}-${selectedSizeVariations.value.height}`)
  }

  return props.sizes.filter(sizeItem => {
    return (
      sizeItem.variations[type] === size
    )
  }).reduce((acc, sizeItem) => acc + sizeItem.stock, 0)
}

const outOfStock = type => {
  selectedSizeVariations.value[type] = null
  emit('show-subscription-form')
  emit('input', null)
}

</script>
<style lang="scss" module>
.product-sizes {
  &__title {
    align-items: center;
    display: inline-flex;
    font-size: $font-size-m;
    margin-bottom: $spacing;
  }

  &__conversion {
    display: inline-flex;
    align-items: center;
    margin-left: $spacing;

    button {
      font-size: $font-size-s;
    }
  }

  &__buttons {
    & > * {
      margin-bottom: $spacing;
      margin-right: $spacing;
    }
  }

  &__notice {
    align-items: center;
    background-color: $white;
    color: $grey-dark;
    font-size: $font-size-s;
    display: flex;
    padding: $spacing 0;
    margin-top: $spacing;

    .svg-icon {
      margin-right: $spacing * 2;
    }
  }
}
</style>
